import type { HelpCenterCategory } from '@/lib/api/help-center.server';

/** Parses the `categoryIds` attribute (CSV) preserving merchant order and dropping duplicates. */
export function parseCategoryIds(value: unknown): string[] {
    if (typeof value !== 'string') {
        return [];
    }

    const ids: string[] = [];
    for (const token of value.split(',')) {
        const id = token.trim();
        if (id && !ids.includes(id)) {
            ids.push(id);
        }
    }

    return ids;
}

/** Turns the Zendesk Guide HTML body into plain text. */
export function stripHtml(value: string): string {
    return value
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/** Lowercases and removes diacritics so "perche" matches "perché". */
export function normalizeSearchText(value: string): string {
    return value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}

/** Splits the typed query into normalized keywords, all of which must match. */
export function parseSearchKeywords(query: string): string[] {
    const normalized = normalizeSearchText(query);
    return normalized ? normalized.split(' ') : [];
}

export interface KeywordMatch {
    /** Offset in the original string, diacritics included. */
    start: number;
    end: number;
}

/**
 * Locates the keywords inside `text` ignoring case and diacritics, and reports the offsets in the
 * original string so the accented characters that matched stay highlighted ("perche" marks "perché").
 * Overlapping occurrences are merged.
 */
export function findKeywordMatches(text: string, keywords: string[]): KeywordMatch[] {
    if (!text || !keywords.length) {
        return [];
    }

    // Normalizing the whole string at once shifts the offsets (é becomes two code points), so the
    // characters are folded one by one while recording where each of them came from.
    let normalized = '';
    const sourceOffsets: number[] = [];
    for (let index = 0; index < text.length; index += 1) {
        const folded = text[index]
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();
        normalized += folded;
        for (let offset = 0; offset < folded.length; offset += 1) {
            sourceOffsets.push(index);
        }
    }

    const matches: KeywordMatch[] = [];
    for (const keyword of keywords) {
        let from = 0;
        let found = normalized.indexOf(keyword, from);
        while (found !== -1) {
            matches.push({
                start: sourceOffsets[found],
                end: sourceOffsets[found + keyword.length - 1] + 1,
            });
            from = found + keyword.length;
            found = normalized.indexOf(keyword, from);
        }
    }

    matches.sort((a, b) => a.start - b.start);

    const merged: KeywordMatch[] = [];
    for (const match of matches) {
        const previous = merged[merged.length - 1];
        if (previous && match.start <= previous.end) {
            previous.end = Math.max(previous.end, match.end);
        } else {
            merged.push({ ...match });
        }
    }
    return merged;
}

function matchesKeywords(article: HelpCenterCategory['sections'][number]['articles'][number], keywords: string[]) {
    const haystack = normalizeSearchText(`${article.title} ${stripHtml(article.body)}`);
    return keywords.every((keyword) => haystack.includes(keyword));
}

/**
 * Prunes the category tree down to the articles matching every keyword: sections without a
 * matching article lose their heading, categories without a matching section lose their tab.
 * An empty query returns the tree untouched.
 */
export function filterHelpCenterCategories(categories: HelpCenterCategory[], query: string): HelpCenterCategory[] {
    const keywords = parseSearchKeywords(query);
    if (!keywords.length) {
        return categories;
    }

    const matching: HelpCenterCategory[] = [];
    for (const category of categories) {
        const sections = category.sections
            .map((section) => ({
                ...section,
                articles: section.articles.filter((article) => matchesKeywords(article, keywords)),
            }))
            .filter((section) => section.articles.length > 0);

        if (sections.length) {
            matching.push({ ...category, sections });
        }
    }

    return matching;
}

/** Total number of articles left in the tree, used for the result count announcement. */
export function countArticles(categories: HelpCenterCategory[]): number {
    return categories.reduce(
        (total, category) =>
            total + category.sections.reduce((sectionTotal, section) => sectionTotal + section.articles.length, 0),
        0
    );
}
